import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  DEFAULT_NOTIFICATIONS_SETTINGS,
  NOTIFICATIONS_SETTINGS,
} from "~/constants";

export enum NotificationSettingsChoices {
  All = "all",
  Likes = "likes",
  Comments = "comments",
  Chat = "chat",
  Follow = "follow",
}

export interface NotificationsSettings {
  [NotificationSettingsChoices.All]: boolean;
  [NotificationSettingsChoices.Likes]: boolean;
  [NotificationSettingsChoices.Comments]: boolean;
  [NotificationSettingsChoices.Chat]: boolean;
  [NotificationSettingsChoices.Follow]: boolean;
}

export interface NotificationsSettingsProps {
  notificationsSettings: NotificationsSettings;
  handleNotificationsSettingsChange: (
    notificationsSettings: NotificationsSettings
  ) => void;
}

export const NotificationsSettingsContext = createContext({
  notificationsSettings: DEFAULT_NOTIFICATIONS_SETTINGS,
} as NotificationsSettingsProps);

export const NotificationsSettingsContextProvider: React.FC = ({
  children,
}) => {
  const [notificationsSettings, setNotificationsSettings] = useState(
    DEFAULT_NOTIFICATIONS_SETTINGS
  );

  const handleNotificationsSettingsChange = useCallback(
    (settings: NotificationsSettings) => {
      setNotificationsSettings(settings);
      localStorage.setItem(NOTIFICATIONS_SETTINGS, JSON.stringify(settings));
    },
    []
  );

  useEffect(() => {
    const settings = localStorage.getItem(NOTIFICATIONS_SETTINGS);
    if (!settings) {
      localStorage.setItem(
        NOTIFICATIONS_SETTINGS,
        JSON.stringify(DEFAULT_NOTIFICATIONS_SETTINGS)
      );
    } else {
      handleNotificationsSettingsChange(JSON.parse(settings));
    }
  }, [handleNotificationsSettingsChange]);

  return (
    <NotificationsSettingsContext.Provider
      value={{ notificationsSettings, handleNotificationsSettingsChange }}
    >
      {children}
    </NotificationsSettingsContext.Provider>
  );
};

export const useNotificationsSettingsContext = () => {
  const { ...values } = useContext(NotificationsSettingsContext);
  return values;
};

export const useNotificationsSettings = () => {
  const {
    notificationsSettings,
    handleNotificationsSettingsChange,
  } = useContext(NotificationsSettingsContext);

  return {
    notificationsSettings,
    handleNotificationsSettingsChange,
  };
};
